import { motion } from 'framer-motion';
import { CheckCircle } from 'lucide-react';
import React, { useState } from 'react';

import { useTheme } from '../../../contexts/ThemeContext';
import { supabase } from '../../../lib/supabase';
import type { CtaData } from '../../../types/blog';

interface CtaProps {
  data: CtaData;
}

/** Hero-gradient card matching the blog page hero — curved, theme-aware, with email subscribe */
const Cta: React.FC<CtaProps> = ({ data }) => {
  const { theme } = useTheme();
  const [email, setEmail] = useState('');
  const [state, setState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setState('error');
      setMessage('Please enter a valid email address.');
      return;
    }
    setState('loading');
    setMessage('');
    try {
      const { data: res, error } = await supabase.functions.invoke('blog-subscribe', {
        body: { email: trimmed, source: 'blog_cta' },
      });
      if (error) throw error;
      if ((res as { error?: string })?.error) throw new Error((res as { error: string }).error);
      setState('success');
      setMessage(
        (res as { alreadySubscribed?: boolean })?.alreadySubscribed
          ? "You're already subscribed — thanks for sticking around!"
          : 'Subscribed! Check your inbox for a welcome email.',
      );
      setEmail('');
    } catch {
      setState('error');
      setMessage('Could not subscribe. Please try again.');
    }
  };

  const title = data.headline?.trim() || 'Stay Updated';
  const sub =
    data.description?.trim() ||
    'Get the latest career insights and job search tips delivered to your inbox.';

  const bg =
    theme === 'dark'
      ? 'linear-gradient(160deg, #1a1a1c 0%, #19191A 60%, #111113 100%)'
      : 'var(--brand-cta-bg)';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="relative my-10 w-full rounded-2xl sm:rounded-3xl overflow-hidden p-8 sm:p-12 text-center shadow-xl"
      style={{ background: bg }}
    >
      {/* decorative blobs matching hero */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden>
        <div className="absolute -top-10 -left-10 w-64 h-64 rounded-full bg-white/5" />
        <div className="absolute top-4 right-4 w-40 h-40 rounded-full bg-white/5" />
        <div className="absolute -bottom-8 left-1/3 w-52 h-52 rounded-full bg-white/5" />
      </div>

      <div className="relative z-10">
        <h3 className="text-2xl sm:text-3xl font-bold text-white mb-3">{title}</h3>
        <p className="text-white/75 mb-8 max-w-2xl mx-auto text-base sm:text-lg leading-relaxed">{sub}</p>

        {state === 'success' ? (
          <div className="flex items-center justify-center gap-2 text-white font-semibold">
            <CheckCircle className="w-5 h-5 text-green-300 flex-shrink-0" aria-hidden />
            {message}
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="flex flex-col sm:flex-row gap-3 max-w-xl mx-auto w-full"
            noValidate
          >
            <input
              type="email"
              required
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (state !== 'idle') setState('idle');
              }}
              placeholder="Enter your email"
              disabled={state === 'loading'}
              autoComplete="email"
              className="flex-1 bg-white/15 backdrop-blur-sm border border-white/30 rounded-xl px-4 py-3 text-white placeholder:text-white/55 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-transparent disabled:opacity-60 min-h-[48px]"
            />
            <button
              type="submit"
              disabled={state === 'loading'}
              className="bg-white text-gray-900 font-semibold px-6 py-3 rounded-xl transition-opacity hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed min-h-[48px] touch-manipulation whitespace-nowrap"
            >
              {state === 'loading' ? 'Subscribing\u2026' : 'Subscribe'}
            </button>
          </form>
        )}

        {message && state !== 'success' && (
          <p
            role="status"
            className={`mt-4 text-sm ${state === 'error' ? 'text-red-300' : 'text-white/70'}`}
          >
            {message}
          </p>
        )}

        {state !== 'success' && (
          <p className="mt-4 text-xs text-white/50">
            We&apos;ll only email you about new blog posts. Unsubscribe any time.
          </p>
        )}
      </div>
    </motion.div>
  );
};

export default Cta;
